/**
 * MembershipCTA — closing section on the homepage (design lock rev 2).
 *
 * Two-column split:
 *   Left  → pitch + inline email subscribe + secondary sign-in link
 *   Right → benefit bullets in a bordered panel + live "active members" chip
 *
 * The inline subscribe hits POST /api/members/subscribe. Duplicate emails are
 * idempotent — the endpoint updates the existing row.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowRight, Lock, FileText, Bookmark, CheckCircle2, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Bullet = ({ icon: Icon, label }) => (
  <div className="flex items-start gap-3 py-2.5">
    <Icon size={14} className="text-[#D92D20] mt-1 shrink-0" />
    <span className="text-[13px] leading-relaxed text-[#B9B2A4]">{label}</span>
  </div>
);

const MembershipCTA = () => {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | loading | success | error
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email || state === "loading") return;
    setState("loading");
    setError("");
    try {
      await axios.post(`${API}/members/subscribe`, { email: email.trim().toLowerCase() }, { timeout: 12000 });
      setState("success");
    } catch (err) {
      setState("error");
      setError(err?.response?.data?.detail || "Subscription failed. Try again.");
    }
  };

  return (
    <section
      data-testid="home-membership-cta"
      className="relative border-t"
      style={{ borderColor: "#2D2A26", background: "#0B0A0D" }}
    >
      {/* Faint background rule */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none opacity-[0.05]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(228,223,211,1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(228,223,211,1) 1px, transparent 1px)
          `,
          backgroundSize: "60px 60px",
          maskImage: "radial-gradient(ellipse 70% 80% at 50% 50%, black 30%, transparent 80%)",
          WebkitMaskImage: "radial-gradient(ellipse 70% 80% at 50% 50%, black 30%, transparent 80%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-start">
          {/* Left — pitch + email subscribe */}
          <div>
            <div className="flex items-center gap-3 mb-3">
              <span className="h-px w-10" style={{ background: "#D92D20" }} />
              <div className="font-mono text-[9px] uppercase tracking-[0.5em] text-[#D92D20]">
                File 003 · Membership
              </div>
            </div>
            <h2 className="font-display text-[28px] sm:text-3xl md:text-5xl lg:text-6xl text-[#E4DFD3] leading-[1.02] tracking-tight">
              Join The Archive.
            </h2>
            <p className="mt-4 md:mt-5 text-[14px] md:text-[16px] leading-relaxed text-[#B9B2A4] max-w-lg">
              Free. Ad-free. No tracking. Bookmark dossiers, follow investigations,
              and receive the weekly brief — a compressed intelligence digest of what
              was published, what got updated, and what to watch next.
            </p>

            {/* Inline subscribe form */}
            <form
              onSubmit={submit}
              data-testid="membership-subscribe-form"
              className="mt-6 md:mt-8 flex flex-col sm:flex-row gap-2 sm:gap-0 max-w-xl"
            >
              <label className="sr-only" htmlFor="membership-email">Email address</label>
              <input
                id="membership-email"
                type="email"
                required
                autoComplete="email"
                data-testid="membership-subscribe-input"
                placeholder="Enter your operative email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={state === "loading" || state === "success"}
                className="flex-1 px-4 py-3.5 bg-transparent border font-mono text-[13px] text-[#E4DFD3]
                           placeholder:text-[#8A8377] focus:outline-none focus:border-[#D92D20] transition-colors"
                style={{ borderColor: state === "error" ? "#D92D20" : "#3A3630" }}
              />
              <button
                type="submit"
                data-testid="membership-subscribe-submit"
                disabled={state === "loading" || state === "success"}
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.35em]
                           border-2 border-[#D92D20] text-[#E4DFD3] hover:bg-[#D92D20] transition-colors disabled:opacity-60"
                style={{ background: "rgba(217,45,32,0.22)" }}
              >
                {state === "loading" ? "Registering…" : state === "success" ? "Registered" : "Join Archive"}
                {state !== "success" && <ArrowRight size={13} />}
              </button>
            </form>

            {/* Form status line */}
            {state === "success" && (
              <div
                data-testid="membership-subscribe-success"
                className="mt-3 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-[#3FB950]"
              >
                <CheckCircle2 size={13} /> Registration acknowledged. Weekly brief will follow.
              </div>
            )}
            {state === "error" && (
              <div
                data-testid="membership-subscribe-error"
                className="mt-3 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]"
              >
                <AlertCircle size={13} /> {error}
              </div>
            )}

            <div className="mt-6 flex items-center gap-4 text-[11px] font-mono uppercase tracking-[0.3em] text-[#7B7168]">
              <Link
                to="/auth/signup"
                data-testid="membership-cta-signup"
                className="hover:text-[#E4DFD3] border-b border-[#454038] hover:border-[#E4DFD3] pb-0.5 transition-colors"
              >
                Create full account
              </Link>
              <span className="text-[#3A3630]">·</span>
              <Link
                to="/auth/login"
                data-testid="membership-cta-login"
                className="hover:text-[#E4DFD3] transition-colors"
              >
                Sign in →
              </Link>
            </div>
          </div>

          {/* Right — benefits panel. Membership telemetry (counts/activity)
              is private by design and never rendered publicly. */}
          <div className="space-y-4 md:space-y-6">
            {/* Community strip — no numbers, no telemetry */}
            <div
              data-testid="membership-community-strip"
              className="border p-3.5 md:p-4 flex items-center justify-between"
              style={{ borderColor: "#2D2A26", background: "#0F0E12" }}
            >
              <div>
                <div className="font-mono text-[9px] uppercase tracking-[0.4em] text-[#8A8377] mb-1">
                  The Archive Community
                </div>
                <div className="font-display text-[17px] md:text-[24px] text-[#E4DFD3] leading-tight">
                  Free to join. Built for investigators.
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#3FB950] animate-pulse" />
                <span className="font-mono text-[9px] uppercase tracking-[0.4em] text-[#3FB950]">
                  Open
                </span>
              </div>
            </div>

            {/* Benefits panel */}
            <div className="border p-5 md:p-8" style={{ borderColor: "#2D2A26", background: "#0F0E12" }}>
              <div className="font-mono text-[9px] uppercase tracking-[0.4em] text-[#8A8377] mb-3 md:mb-4">
                // Membership Benefits
              </div>
              <Bullet icon={FileText} label="Full-text access to every dossier, including archived sub-sections and evidence appendices." />
              <Bullet icon={Bookmark} label="Bookmark, follow, and comment on investigations. Get notified when a dossier is updated." />
              <Bullet icon={Lock}     label="Encrypted account. Never resold. No advertising. Nothing to cancel — The Archive is free." />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MembershipCTA;
